import _ from 'lodash';

import { CallStatus, RenderingContainerType, UserStatus } from 'constants/telephony';

import { RESET_STATE } from 'store/global/actions';

import { TELEPHONY_ACTION_TYPE } from '../actions/telephony.actions';

const initialCallState = {
  status: CallStatus.IDLE,
  telnyxCallState: null,
  dialedNumber: '',
  callerName: null,
  callerNumber: null,
  callId: null,
  startTime: null,
  isOnHold: false,
  isMuted: false,
  hangupCause: null,
};

const initialTelnyxState = {
  isConnected: false,
  isConnecting: false,
  hasEverConnected: false,
  error: null,
};

const initialContactsState = {
  list: [],
  isLoading: false,
  searchQuery: '',
};

const initialCallHistoryState = {
  list: [],
  isLoading: false,
  filter: 'all',
};

const initialSignalRState = {
  isConnected: false,
  isConnecting: false,
  isReconnecting: false,
};

const initialLeadNotificationState = {
  isVisible: false,
  notificationId: null,
  leadData: null,
  clientState: null,
  isLiveTransfer: false,
  receivedAt: null,
  isAccepting: false,
  isRejecting: false,
};

const initialTextMessagingState = {
  leads: {
    list: [],
    isLoading: false,
    searchQuery: '',
  },
  activeConversation: {
    lead: null,
    messages: [],
    isLoading: false,
    isSending: false,
  },
};

const initialBorrowerChatState = {
  isInitialized: false,
  unreadCount: 0,
  activeChannelUrl: null,
};

const initialInternalChatState = {
  unreadCount: 0,
  activeChannelUrl: null,
};

const initialAudioDeviceSettingsState = {
  selectedInputDeviceId: null,
  selectedOutputDeviceId: null,
  availableInputDevices: [],
  availableOutputDevices: [],
  permissionState: 'prompt',
};

const initialVoicemailGreetingState = {
  activeGreetingId: null,
  isLoading: false,
  isSaving: false,
};

const initialVoicemailInboxState = {
  list: [],
  unreadCount: 0,
  isLoading: false,
  totalCount: 0,
};

const initialState = {
  isWidgetOpen: false,
  activeTab: RenderingContainerType.CONTACTS,
  isAvailable: false,
  telephonyUser: {
    user: null,
    isLoading: false,
  },
  call: { ...initialCallState },
  telnyx: { ...initialTelnyxState },
  dialedNumber: '',
  previousStatus: null,
  contacts: { ...initialContactsState },
  callHistory: { ...initialCallHistoryState },
  signalR: { ...initialSignalRState },
  leadNotification: { ...initialLeadNotificationState },
  textMessaging: { ...initialTextMessagingState },
  borrowerChat: { ...initialBorrowerChatState },
  internalChat: { ...initialInternalChatState },
  audioDeviceSettings: { ...initialAudioDeviceSettingsState },
  voicemailGreeting: { ...initialVoicemailGreetingState },
  voicemailInbox: { ...initialVoicemailInboxState },
};

const telephonyReducer = function (state = initialState, { type, payload }) {
  switch (type) {
    case TELEPHONY_ACTION_TYPE.SET_WIDGET_OPEN:
      return { ...state, isWidgetOpen: payload };

    case TELEPHONY_ACTION_TYPE.SET_ACTIVE_TAB:
      return { ...state, activeTab: payload };

    case TELEPHONY_ACTION_TYPE.FETCH_USER_PENDING:
      return { ...state, telephonyUser: { ...state.telephonyUser, isLoading: true } };

    case TELEPHONY_ACTION_TYPE.FETCH_USER_FULFILLED:
      return {
        ...state,
        telephonyUser: { user: payload, isLoading: false },
        isAvailable: payload?.contactStatus === UserStatus.AVAILABLE,
      };

    case TELEPHONY_ACTION_TYPE.FETCH_USER_REJECTED:
      return { ...state, telephonyUser: { ...state.telephonyUser, isLoading: false } };

    case TELEPHONY_ACTION_TYPE.UPDATE_USER_STATUS:
      return {
        ...state,
        telephonyUser: {
          ...state.telephonyUser,
          user: state.telephonyUser.user
            ? { ...state.telephonyUser.user, contactStatus: payload }
            : { contactStatus: payload },
        },
        isAvailable: payload === UserStatus.AVAILABLE,
      };

    // Telnyx connection state
    case TELEPHONY_ACTION_TYPE.TELNYX_CONNECTING:
      return {
        ...state,
        telnyx: { ...state.telnyx, isConnecting: true, error: null },
      };

    case TELEPHONY_ACTION_TYPE.TELNYX_CONNECTED:
      return {
        ...state,
        telnyx: { isConnected: true, isConnecting: false, hasEverConnected: true, error: null },
      };

    case TELEPHONY_ACTION_TYPE.TELNYX_DISCONNECTED:
      return {
        ...state,
        telnyx: { ...state.telnyx, isConnected: false, isConnecting: false, error: null },
      };

    case TELEPHONY_ACTION_TYPE.TELNYX_ERROR:
      return {
        ...state,
        telnyx: { ...state.telnyx, isConnecting: false, error: payload },
      };

    // Call state
    case TELEPHONY_ACTION_TYPE.CALL_OUTGOING:
      return {
        ...state,
        call: {
          ...state.call,
          status: CallStatus.OUTGOING,
          dialedNumber: payload.dialedNumber,
          callId: payload.callId ?? state.call.callId,
        },
        // Only capture previousStatus on the first dispatch; subsequent dispatches
        // (e.g. with callId) must not overwrite it with the already-changed ON_CALL status.
        previousStatus: state.previousStatus ?? state.telephonyUser.user?.contactStatus ?? null,
      };

    case TELEPHONY_ACTION_TYPE.CALL_INCOMING: {
      // Guard: do not overwrite an already-active, outgoing, or already-incoming call
      if (
        state.call.status === CallStatus.ACTIVE ||
        state.call.status === CallStatus.OUTGOING ||
        state.call.status === CallStatus.INCOMING
      ) {
        return state;
      }

      return {
        ...state,
        call: {
          ...state.call,
          status: CallStatus.INCOMING,
          callId: payload.callId,
          callerName: payload.callerName,
          callerNumber: payload.callerNumber,
        },
        previousStatus: state.telephonyUser.user?.contactStatus ?? null,
      };
    }

    case TELEPHONY_ACTION_TYPE.CALL_ACTIVE:
      return {
        ...state,
        call: {
          ...state.call,
          status: CallStatus.ACTIVE,
          callId: payload.callId ?? state.call.callId,
          startTime: state.call.startTime ?? payload.startTime,
        },
      };

    case TELEPHONY_ACTION_TYPE.CALL_HANGUP: {
      // Guard: ignore duplicate HANGUP dispatches (e.g. from TelnyxProvider after
      // hangupCallAction already transitioned to POST_CALL or IDLE).
      if (state.call.status === CallStatus.POST_CALL || state.call.status === CallStatus.IDLE) {
        return state;
      }

      // Only show PostCallForm if the call was actually connected (ACTIVE).
      // For calls that were never answered (INCOMING declined, OUTGOING not picked up),
      // transition directly to IDLE.
      const wasConnected = state.call.status === CallStatus.ACTIVE;
      const restoredStatus = state.previousStatus ?? state.telephonyUser.user?.contactStatus;

      return {
        ...state,
        call: {
          ...state.call,
          status: wasConnected ? CallStatus.POST_CALL : CallStatus.IDLE,
          hangupCause: payload?.cause ?? null,
        },
        // Restore the user's status from before the call started
        telephonyUser: restoredStatus
          ? {
              ...state.telephonyUser,
              user: state.telephonyUser.user
                ? { ...state.telephonyUser.user, contactStatus: restoredStatus }
                : { contactStatus: restoredStatus },
            }
          : state.telephonyUser,
        isAvailable: restoredStatus === UserStatus.AVAILABLE,
        // Clear previousStatus when going to IDLE (unanswered calls);
        // keep it when going to POST_CALL so CALL_RESET can clear it later.
        previousStatus: wasConnected ? state.previousStatus : null,
      };
    }

    case TELEPHONY_ACTION_TYPE.CALL_DESTROY: {
      // Ignore DESTROY if we're in POST_CALL — user needs to fill out the form first
      if (state.call.status === CallStatus.POST_CALL) {
        return state;
      }

      return {
        ...state,
        call: { ...initialCallState },
        previousStatus: null,
      };
    }

    case TELEPHONY_ACTION_TYPE.CALL_HOLD:
      return {
        ...state,
        call: { ...state.call, isOnHold: true },
      };

    case TELEPHONY_ACTION_TYPE.CALL_UNHOLD:
      return {
        ...state,
        call: { ...state.call, isOnHold: false },
      };

    case TELEPHONY_ACTION_TYPE.CALL_MUTE:
      return {
        ...state,
        call: { ...state.call, isMuted: true },
      };

    case TELEPHONY_ACTION_TYPE.CALL_UNMUTE:
      return {
        ...state,
        call: { ...state.call, isMuted: false },
      };

    case TELEPHONY_ACTION_TYPE.CALL_STATE_UPDATE:
      return {
        ...state,
        call: {
          ...state.call,
          telnyxCallState: payload.telnyxCallState,
        },
      };

    case TELEPHONY_ACTION_TYPE.CALL_RESET:
      return {
        ...state,
        call: { ...initialCallState },
        previousStatus: null,
      };

    case TELEPHONY_ACTION_TYPE.SET_DIALED_NUMBER:
      return {
        ...state,
        dialedNumber: payload,
      };

    // Contacts
    case TELEPHONY_ACTION_TYPE.FETCH_CONTACTS_PENDING:
      return {
        ...state,
        contacts: { ...state.contacts, isLoading: true },
      };

    case TELEPHONY_ACTION_TYPE.FETCH_CONTACTS_FULFILLED:
      return {
        ...state,
        contacts: { ...state.contacts, list: payload ?? [], isLoading: false },
      };

    case TELEPHONY_ACTION_TYPE.FETCH_CONTACTS_REJECTED:
      return {
        ...state,
        contacts: { ...state.contacts, isLoading: false },
      };

    case TELEPHONY_ACTION_TYPE.SET_CONTACTS_SEARCH:
      return {
        ...state,
        contacts: { ...state.contacts, searchQuery: payload },
      };

    case TELEPHONY_ACTION_TYPE.UPDATE_CONTACT_STATUS: {
      const updatedList = state.contacts.list.map(contact =>
        contact.userId === payload.userId
          ? { ...contact, contactStatus: payload.contactStatus }
          : contact,
      );

      return {
        ...state,
        contacts: { ...state.contacts, list: updatedList },
      };
    }

    // Call History
    case TELEPHONY_ACTION_TYPE.FETCH_CALL_HISTORY_PENDING:
      return {
        ...state,
        callHistory: { ...state.callHistory, isLoading: true },
      };

    case TELEPHONY_ACTION_TYPE.FETCH_CALL_HISTORY_FULFILLED:
      return {
        ...state,
        callHistory: { ...state.callHistory, list: payload ?? [], isLoading: false },
      };

    case TELEPHONY_ACTION_TYPE.FETCH_CALL_HISTORY_REJECTED:
      return {
        ...state,
        callHistory: { ...state.callHistory, isLoading: false },
      };

    case TELEPHONY_ACTION_TYPE.SET_CALL_HISTORY_FILTER:
      return {
        ...state,
        callHistory: { ...state.callHistory, filter: payload },
      };

    // SignalR connection state
    case TELEPHONY_ACTION_TYPE.SIGNALR_CONNECTING:
      return {
        ...state,
        signalR: { ...state.signalR, isConnecting: true, isReconnecting: false },
      };

    case TELEPHONY_ACTION_TYPE.SIGNALR_CONNECTED:
      return {
        ...state,
        signalR: { isConnected: true, isConnecting: false, isReconnecting: false },
      };

    case TELEPHONY_ACTION_TYPE.SIGNALR_DISCONNECTED:
      return {
        ...state,
        signalR: { isConnected: false, isConnecting: false, isReconnecting: false },
      };

    case TELEPHONY_ACTION_TYPE.SIGNALR_RECONNECTING:
      return {
        ...state,
        signalR: { ...state.signalR, isConnected: false, isReconnecting: true },
      };

    // Lead notification
    case TELEPHONY_ACTION_TYPE.SHOW_LEAD_NOTIFICATION:
      return {
        ...state,
        leadNotification: {
          ...initialLeadNotificationState,
          isVisible: true,
          notificationId: payload.notificationId,
          leadData: payload.leadData,
          clientState: payload.clientState,
          isLiveTransfer: payload.isLiveTransfer ?? false,
          receivedAt: payload.receivedAt,
        },
      };

    case TELEPHONY_ACTION_TYPE.DISMISS_LEAD_NOTIFICATION: {
      if (
        payload?.notificationId &&
        state.leadNotification.notificationId !== payload.notificationId
      ) {
        return state;
      }

      return {
        ...state,
        leadNotification: { ...initialLeadNotificationState },
      };
    }

    case TELEPHONY_ACTION_TYPE.ACCEPT_LEAD_PENDING:
      return {
        ...state,
        leadNotification: { ...state.leadNotification, isAccepting: true },
      };

    case TELEPHONY_ACTION_TYPE.ACCEPT_LEAD_FULFILLED:
    case TELEPHONY_ACTION_TYPE.REJECT_LEAD_FULFILLED:
    case TELEPHONY_ACTION_TYPE.AUTO_REJECT_LEAD:
    case TELEPHONY_ACTION_TYPE.LEAD_NOTIFICATION_TIMEOUT:
      return {
        ...state,
        leadNotification: { ...initialLeadNotificationState },
      };

    case TELEPHONY_ACTION_TYPE.ACCEPT_LEAD_REJECTED:
      return {
        ...state,
        leadNotification: { ...state.leadNotification, isAccepting: false },
      };

    case TELEPHONY_ACTION_TYPE.REJECT_LEAD_PENDING:
      return {
        ...state,
        leadNotification: { ...state.leadNotification, isRejecting: true },
      };

    case TELEPHONY_ACTION_TYPE.REJECT_LEAD_REJECTED:
      return {
        ...state,
        leadNotification: { ...state.leadNotification, isRejecting: false },
      };

    // Text messaging (DEV-1453)
    case TELEPHONY_ACTION_TYPE.FETCH_TEXT_LEADS_PENDING:
      return {
        ...state,
        textMessaging: {
          ...state.textMessaging,
          leads: { ...state.textMessaging.leads, isLoading: true },
        },
      };

    case TELEPHONY_ACTION_TYPE.FETCH_TEXT_LEADS_FULFILLED:
      return {
        ...state,
        textMessaging: {
          ...state.textMessaging,
          leads: { ...state.textMessaging.leads, list: payload ?? [], isLoading: false },
        },
      };

    case TELEPHONY_ACTION_TYPE.FETCH_TEXT_LEADS_REJECTED:
      return {
        ...state,
        textMessaging: {
          ...state.textMessaging,
          leads: { ...state.textMessaging.leads, isLoading: false },
        },
      };

    case TELEPHONY_ACTION_TYPE.SET_TEXT_LEADS_SEARCH:
      return {
        ...state,
        textMessaging: {
          ...state.textMessaging,
          leads: { ...state.textMessaging.leads, searchQuery: payload },
        },
      };

    case TELEPHONY_ACTION_TYPE.SET_ACTIVE_CONVERSATION:
      return {
        ...state,
        textMessaging: {
          ...state.textMessaging,
          activeConversation: {
            ...initialTextMessagingState.activeConversation,
            lead: payload,
          },
        },
      };

    case TELEPHONY_ACTION_TYPE.FETCH_TEXT_MESSAGES_PENDING:
      return {
        ...state,
        textMessaging: {
          ...state.textMessaging,
          activeConversation: {
            ...state.textMessaging.activeConversation,
            isLoading: true,
          },
        },
      };

    case TELEPHONY_ACTION_TYPE.FETCH_TEXT_MESSAGES_FULFILLED:
      return {
        ...state,
        textMessaging: {
          ...state.textMessaging,
          activeConversation: {
            ...state.textMessaging.activeConversation,
            messages: payload ?? [],
            isLoading: false,
          },
        },
      };

    case TELEPHONY_ACTION_TYPE.FETCH_TEXT_MESSAGES_REJECTED:
      return {
        ...state,
        textMessaging: {
          ...state.textMessaging,
          activeConversation: {
            ...state.textMessaging.activeConversation,
            isLoading: false,
          },
        },
      };

    case TELEPHONY_ACTION_TYPE.SEND_TEXT_MESSAGE_PENDING:
      return {
        ...state,
        textMessaging: {
          ...state.textMessaging,
          activeConversation: {
            ...state.textMessaging.activeConversation,
            isSending: true,
          },
        },
      };

    case TELEPHONY_ACTION_TYPE.SEND_TEXT_MESSAGE_FULFILLED:
      return {
        ...state,
        textMessaging: {
          ...state.textMessaging,
          activeConversation: {
            ...state.textMessaging.activeConversation,
            messages: [...state.textMessaging.activeConversation.messages, payload],
            isSending: false,
          },
        },
      };

    case TELEPHONY_ACTION_TYPE.SEND_TEXT_MESSAGE_REJECTED:
      return {
        ...state,
        textMessaging: {
          ...state.textMessaging,
          activeConversation: {
            ...state.textMessaging.activeConversation,
            isSending: false,
          },
        },
      };

    case TELEPHONY_ACTION_TYPE.CLEAR_ACTIVE_CONVERSATION:
      return {
        ...state,
        textMessaging: {
          ...state.textMessaging,
          activeConversation: { ...initialTextMessagingState.activeConversation },
        },
      };

    case TELEPHONY_ACTION_TYPE.TEXT_MESSAGE_RECEIVED: {
      const activeLead = state.textMessaging.activeConversation.lead;
      const isForActiveConversation =
        activeLead &&
        (payload.phoneNumberFrom === activeLead.phoneNumber ||
          payload.phoneNumberTo === activeLead.phoneNumber);

      if (isForActiveConversation) {
        return {
          ...state,
          textMessaging: {
            ...state.textMessaging,
            activeConversation: {
              ...state.textMessaging.activeConversation,
              messages: [...state.textMessaging.activeConversation.messages, payload],
            },
          },
        };
      }

      return state;
    }

    // Borrower chat (DEV-1454)
    case TELEPHONY_ACTION_TYPE.SET_BORROWER_CHAT_INITIALIZED:
      return {
        ...state,
        borrowerChat: { ...state.borrowerChat, isInitialized: payload },
      };

    case TELEPHONY_ACTION_TYPE.SET_BORROWER_CHAT_UNREAD_COUNT:
      return {
        ...state,
        borrowerChat: { ...state.borrowerChat, unreadCount: payload },
      };

    case TELEPHONY_ACTION_TYPE.SET_BORROWER_CHAT_ACTIVE_CHANNEL:
      return {
        ...state,
        borrowerChat: { ...state.borrowerChat, activeChannelUrl: payload },
      };

    // Internal chat (DEV-1455)
    case TELEPHONY_ACTION_TYPE.SET_INTERNAL_CHAT_UNREAD_COUNT:
      return {
        ...state,
        internalChat: { ...state.internalChat, unreadCount: payload },
      };

    case TELEPHONY_ACTION_TYPE.SET_INTERNAL_CHAT_ACTIVE_CHANNEL:
      return {
        ...state,
        internalChat: { ...state.internalChat, activeChannelUrl: payload },
      };

    // Audio device settings (DEV-1482)
    case TELEPHONY_ACTION_TYPE.SET_AVAILABLE_AUDIO_DEVICES:
      return {
        ...state,
        audioDeviceSettings: {
          ...state.audioDeviceSettings,
          availableInputDevices: payload.inputDevices,
          availableOutputDevices: payload.outputDevices,
        },
      };

    case TELEPHONY_ACTION_TYPE.SET_SELECTED_INPUT_DEVICE:
      return {
        ...state,
        audioDeviceSettings: {
          ...state.audioDeviceSettings,
          selectedInputDeviceId: payload,
        },
      };

    case TELEPHONY_ACTION_TYPE.SET_SELECTED_OUTPUT_DEVICE:
      return {
        ...state,
        audioDeviceSettings: {
          ...state.audioDeviceSettings,
          selectedOutputDeviceId: payload,
        },
      };

    case TELEPHONY_ACTION_TYPE.SET_AUDIO_PERMISSION_STATE:
      return {
        ...state,
        audioDeviceSettings: {
          ...state.audioDeviceSettings,
          permissionState: payload,
        },
      };

    // Voicemail greeting (DEV-1575)
    case TELEPHONY_ACTION_TYPE.FETCH_ACTIVE_GREETING_PENDING:
      return {
        ...state,
        voicemailGreeting: { ...state.voicemailGreeting, isLoading: true },
      };

    case TELEPHONY_ACTION_TYPE.FETCH_ACTIVE_GREETING_FULFILLED:
      return {
        ...state,
        voicemailGreeting: {
          ...state.voicemailGreeting,
          activeGreetingId: payload?.audioRecordingId ?? null,
          isLoading: false,
        },
      };

    case TELEPHONY_ACTION_TYPE.FETCH_ACTIVE_GREETING_REJECTED:
      return {
        ...state,
        voicemailGreeting: { ...state.voicemailGreeting, isLoading: false },
      };

    case TELEPHONY_ACTION_TYPE.SET_ACTIVE_GREETING_PENDING:
    case TELEPHONY_ACTION_TYPE.REMOVE_ACTIVE_GREETING_PENDING:
      return {
        ...state,
        voicemailGreeting: { ...state.voicemailGreeting, isSaving: true },
      };

    case TELEPHONY_ACTION_TYPE.SET_ACTIVE_GREETING_FULFILLED:
      return {
        ...state,
        voicemailGreeting: {
          ...state.voicemailGreeting,
          activeGreetingId: payload,
          isSaving: false,
        },
      };

    case TELEPHONY_ACTION_TYPE.REMOVE_ACTIVE_GREETING_FULFILLED:
      return {
        ...state,
        voicemailGreeting: {
          ...state.voicemailGreeting,
          activeGreetingId: null,
          isSaving: false,
        },
      };

    case TELEPHONY_ACTION_TYPE.SET_ACTIVE_GREETING_REJECTED:
    case TELEPHONY_ACTION_TYPE.REMOVE_ACTIVE_GREETING_REJECTED:
      return {
        ...state,
        voicemailGreeting: { ...state.voicemailGreeting, isSaving: false },
      };

    // Voicemail inbox (DEV-1577)
    case TELEPHONY_ACTION_TYPE.FETCH_VOICEMAILS_PENDING:
      return {
        ...state,
        voicemailInbox: { ...state.voicemailInbox, isLoading: true },
      };

    case TELEPHONY_ACTION_TYPE.FETCH_VOICEMAILS_FULFILLED: {
      const rawList = payload?.items ?? payload ?? [];
      const normalizedList = rawList.map(vm => ({
        ...vm,
        voicemailId: vm.voicemailId ?? vm.voicemailMessageId ?? vm.id,
        callerNumber: vm.callerNumber ?? vm.callerPhoneNumber,
        duration: vm.duration ?? vm.durationSeconds,
        createdAt: vm.createdAt ?? vm.createdDate,
      }));

      return {
        ...state,
        voicemailInbox: {
          ...state.voicemailInbox,
          list: normalizedList,
          totalCount: payload?.totalCount ?? rawList.length,
          isLoading: false,
        },
      };
    }

    case TELEPHONY_ACTION_TYPE.FETCH_VOICEMAILS_REJECTED:
      return {
        ...state,
        voicemailInbox: { ...state.voicemailInbox, isLoading: false },
      };

    case TELEPHONY_ACTION_TYPE.FETCH_VOICEMAIL_UNREAD_COUNT_FULFILLED:
      return {
        ...state,
        voicemailInbox: {
          ...state.voicemailInbox,
          unreadCount: _.isNumber(payload) ? payload : (payload?.count ?? 0),
        },
      };

    case TELEPHONY_ACTION_TYPE.MARK_VOICEMAIL_READ_FULFILLED: {
      const wasAlreadyRead =
        state.voicemailInbox.list.find(vm => vm.voicemailId === payload)?.isRead !== false;

      return {
        ...state,
        voicemailInbox: {
          ...state.voicemailInbox,
          list: state.voicemailInbox.list.map(vm =>
            vm.voicemailId === payload ? { ...vm, isRead: true } : vm,
          ),
          unreadCount: wasAlreadyRead
            ? state.voicemailInbox.unreadCount
            : Math.max(0, state.voicemailInbox.unreadCount - 1),
        },
      };
    }

    case TELEPHONY_ACTION_TYPE.MARK_ALL_VOICEMAILS_READ_FULFILLED:
      return {
        ...state,
        voicemailInbox: {
          ...state.voicemailInbox,
          list: state.voicemailInbox.list.map(vm => ({ ...vm, isRead: true })),
          unreadCount: 0,
        },
      };

    case TELEPHONY_ACTION_TYPE.DELETE_VOICEMAIL_FULFILLED: {
      const wasUnread =
        state.voicemailInbox.list.find(vm => vm.voicemailId === payload)?.isRead === false;

      return {
        ...state,
        voicemailInbox: {
          ...state.voicemailInbox,
          list: state.voicemailInbox.list.filter(vm => vm.voicemailId !== payload),
          unreadCount: wasUnread
            ? Math.max(0, state.voicemailInbox.unreadCount - 1)
            : state.voicemailInbox.unreadCount,
          totalCount: Math.max(0, state.voicemailInbox.totalCount - 1),
        },
      };
    }

    case TELEPHONY_ACTION_TYPE.VOICEMAIL_RECEIVED: {
      const normalizedVoicemail = {
        ...payload,
        voicemailId: payload.voicemailId ?? payload.voicemailMessageId ?? payload.id,
        callerNumber: payload.callerNumber ?? payload.callerPhoneNumber,
        duration: payload.duration ?? payload.durationSeconds,
        createdAt: payload.createdAt ?? payload.createdDate,
        isRead: payload.isRead ?? false,
      };

      return {
        ...state,
        voicemailInbox: {
          ...state.voicemailInbox,
          list: [normalizedVoicemail, ...state.voicemailInbox.list],
          unreadCount: state.voicemailInbox.unreadCount + 1,
          totalCount: state.voicemailInbox.totalCount + 1,
        },
      };
    }

    case RESET_STATE:
      return initialState;

    default:
      return state;
  }
};

export default telephonyReducer;
